import { supabase } from '../../../lib/supabase'
import {
  ChwAction, ChwLeadPayload, ChwOrg, ChwReportData, ChwReportMeta, ChwRun,
  ChwSettings, ReportKind,
} from '../types'

// All queries run as the signed-in user; RLS restricts every table to orgs
// the user is a member of (chw_org_members), so this service never needs to
// filter by membership itself.

export async function getMyOrgs(): Promise<ChwOrg[]> {
  const { data, error } = await supabase
    .from('chw_orgs')
    .select('id, org_key, display_name, is_active, settings')
    .eq('is_active', true)
    .order('display_name')
  if (error) throw error
  return (data || []) as ChwOrg[]
}

export async function getReports(orgId: string): Promise<ChwReportMeta[]> {
  const { data, error } = await supabase
    .from('chw_reports')
    .select('id, kind, report_date, window_since, subject, created_at')
    .eq('org_id', orgId)
    .order('report_date', { ascending: false })
    .order('kind')
    .limit(60)
  if (error) throw error
  return (data || []) as ChwReportMeta[]
}

export async function getReportHtml(reportId: string): Promise<string> {
  const { data, error } = await supabase
    .from('chw_reports')
    .select('html')
    .eq('id', reportId)
    .single()
  if (error) throw error
  return (data as { html: string }).html
}

/** Structured report payload for the native (filterable) report view.
 * Returns null for reports uploaded before the worker wrote data jsonb. */
export async function getReportData(reportId: string): Promise<ChwReportData | null> {
  const { data, error } = await supabase
    .from('chw_reports')
    .select('data')
    .eq('id', reportId)
    .single()
  if (error) throw error
  return ((data as { data: ChwReportData | null }).data) || null
}

export async function getRuns(orgId: string): Promise<ChwRun[]> {
  const { data, error } = await supabase
    .from('chw_runs')
    .select('id, org_id, kind, status, created_at, started_at, finished_at, error, summary')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(20)
  if (error) throw error
  return (data || []) as ChwRun[]
}

export async function requestRun(
  orgId: string, userId: string, kind: ReportKind
): Promise<void> {
  const { error } = await supabase.from('chw_runs').insert({
    org_id: orgId,
    kind,
    status: 'requested',
    requested_by: userId,
    params: { kinds: [kind] },
  })
  if (error) throw error
}

export async function getActions(orgId: string): Promise<ChwAction[]> {
  const { data, error } = await supabase
    .from('chw_actions')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false })
    .limit(200)
  if (error) throw error
  return (data || []) as ChwAction[]
}

/** Queue "create this lead-gen company in HubSpot as a lead" for the worker
 * (the portal holds no HubSpot tokens, so this cannot run in the browser). */
export async function requestCreateLead(
  orgId: string, userId: string, payload: ChwLeadPayload
): Promise<void> {
  const { error } = await supabase.from('chw_actions').insert({
    org_id: orgId,
    type: 'create_lead',
    status: 'requested',
    requested_by: userId,
    payload,
  })
  if (error) throw error
}

export async function saveSettings(
  orgId: string, settings: ChwSettings
): Promise<void> {
  const { error } = await supabase
    .from('chw_orgs')
    .update({ settings })
    .eq('id', orgId)
  if (error) throw error
}
